import React, { useState } from "react";
import { HiX, HiPlus, HiTrash } from "react-icons/hi";
import { 
  SuccessMetric, 
  AnswerType, 
  ChartType, 
  MetricPriority, 
  MetricCategory 
} from "../../services/api" ;
import slugify from "slugify";

interface CustomMetricModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (metric: SuccessMetric) => void;
}

const CustomMetricModal: React.FC<CustomMetricModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [question, setQuestion] = useState("");
  const [answerType, setAnswerType] = useState<AnswerType>(AnswerType.YES_NO);
  const [category, setCategory] = useState<MetricCategory>(MetricCategory.OUTCOME);
  const [priority, setPriority] = useState<MetricPriority>(MetricPriority.MEDIUM);
  const [reason, setReason] = useState("");
  const [chartType, setChartType] = useState<ChartType>(ChartType.PIE);
  const [extractionHints, setExtractionHints] = useState("");
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState("");
  const [config, setConfig] = useState<Record<string, any>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnumType = [AnswerType.SINGLE_SELECT, AnswerType.MULTI_SELECT].includes(answerType);
  const isScaleType = [AnswerType.SCALE_5, AnswerType.SCALE_10].includes(answerType);
  const isNPSType = answerType === AnswerType.NPS;
  const isCurrencyType = answerType === AnswerType.CURRENCY;

  const resetForm = () => {
    setQuestion("");
    setAnswerType(AnswerType.YES_NO);
    setCategory(MetricCategory.OUTCOME);
    setPriority(MetricPriority.MEDIUM);
    setReason("");
    setChartType(ChartType.PIE);
    setExtractionHints("");
    setOptions([]);
    setNewOption("");
    setConfig({});
    setValidationError(null);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const addOption = () => {
    if (newOption.trim() && !options.includes(newOption.trim())) {
      setOptions([...options, newOption.trim()]);
      setNewOption("");
      if (validationError) setValidationError(null);
    }
  };

  const removeOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const getDefaultConfig = (type: AnswerType): Record<string, any> => {
    switch (type) {
      case AnswerType.SCALE_5:
        return { min: 1, max: 5, labels: ["Poor", "Excellent"] };
      case AnswerType.SCALE_10:
        return { min: 1, max: 10, labels: ["Poor", "Excellent"] };
      case AnswerType.NPS:
        return { min: 0, max: 10, labels: ["Not likely", "Very likely"] };
      case AnswerType.CURRENCY:
        return { currency: "INR" };
      case AnswerType.SINGLE_SELECT:
      case AnswerType.MULTI_SELECT:
        return { include_other: true };
      case AnswerType.TEXT_SHORT:
        return { max_length: 50 };
      case AnswerType.TEXT_LONG:
        return { max_length: 500 };
      default:
        return {};
    }
  };

  const handleAnswerTypeChange = (newType: AnswerType) => {
    setAnswerType(newType);
    setConfig(getDefaultConfig(newType));
    
    // Set appropriate chart type
    const chartMapping: Record<AnswerType, ChartType> = {
      [AnswerType.YES_NO]: ChartType.PIE,
      [AnswerType.TRUE_FALSE]: ChartType.PIE,
      [AnswerType.PRESENT_ABSENT]: ChartType.PIE,
      [AnswerType.SINGLE_SELECT]: ChartType.BAR,
      [AnswerType.MULTI_SELECT]: ChartType.BAR,
      [AnswerType.SCALE_5]: ChartType.GAUGE,
      [AnswerType.SCALE_10]: ChartType.GAUGE,
      [AnswerType.NPS]: ChartType.GAUGE,
      [AnswerType.SENTIMENT]: ChartType.PIE,
      [AnswerType.NUMBER]: ChartType.METRIC,
      [AnswerType.CURRENCY]: ChartType.METRIC,
      [AnswerType.PERCENTAGE]: ChartType.GAUGE,
      [AnswerType.DURATION_IN_MINUTES]: ChartType.METRIC,
      [AnswerType.TEXT_SHORT]: ChartType.METRIC,
      [AnswerType.TEXT_LONG]: ChartType.METRIC,
    };
    setChartType(chartMapping[newType] || ChartType.PIE);
    
    // Reset options for non-enum types
    if (![AnswerType.SINGLE_SELECT, AnswerType.MULTI_SELECT].includes(newType)) {
      setOptions([]);
    }
  };

  const validateForm = (): string | null => {
    if (!question.trim()) return "Question is required";
    if (question.length < 10) return "Question must be at least 10 characters";
    if (question.length > 500) return "Question must be less than 500 characters";
    if (!reason.trim()) return "Reason is required";
    if (reason.length < 20) return "Reason must be at least 20 characters";
    if (isEnumType && options.length < 2) return "At least 2 options are required for selection types";
    return null;
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      setValidationError(error);
      return;
    }

    setIsSubmitting(true);
    setValidationError(null);

    try {
      const metric: SuccessMetric = {
        question: question.trim(),
        slug: slugify(question.trim(), { lower: true, replacement: "-", }),  // TODO: Add in backend
        answer_type: answerType,
        category,
        priority,
        reason: reason.trim(),
        chart_type: chartType,
        extraction_hints: extractionHints.trim() || undefined,
        options: isEnumType && options.length > 0 ? options : undefined,
        config: Object.keys(config).length > 0 ? config : undefined,
      };

      onSave(metric);
      handleClose();
    } catch (err) {
      setValidationError("Failed to add question. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Create Custom Question</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <HiX size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Question Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Question Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={question}
              onChange={(e) => {
                setQuestion(e.target.value);
                if (validationError) setValidationError(null);
              }}
              placeholder="e.g., Did the merchant request additional training?"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                question.length > 0 && question.length < 10 ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              maxLength={500}
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                This is what the AI will look for in the conversation
              </p>
              <p className={`text-xs ${
                question.length < 10 ? 'text-red-500' : 'text-gray-400'
              }`}>
                {question.length}/10 min
              </p>
            </div>
            {question.length > 0 && question.length < 10 && (
              <p className="text-xs text-red-500 mt-1">Question must be at least 10 characters</p>
            )}
          </div>

          {/* Answer Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Answer Type <span className="text-red-500">*</span>
            </label>
            <select
              value={answerType}
              onChange={(e) => handleAnswerTypeChange(e.target.value as AnswerType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <optgroup label="Binary Types">
                <option value={AnswerType.YES_NO}>Yes/No</option>
                <option value={AnswerType.TRUE_FALSE}>True/False</option>
                <option value={AnswerType.PRESENT_ABSENT}>Present/Absent</option>
              </optgroup>
              <optgroup label="Selection Types">
                <option value={AnswerType.SINGLE_SELECT}>Single Choice</option>
                <option value={AnswerType.MULTI_SELECT}>Multiple Choice</option>
              </optgroup>
              <optgroup label="Rating Types">
                <option value={AnswerType.SCALE_5}>Scale (1-5)</option>
                <option value={AnswerType.SCALE_10}>Scale (1-10)</option>
                <option value={AnswerType.NPS}>NPS Score (0-10)</option>
                <option value={AnswerType.SENTIMENT}>Sentiment (Positive|Negative|Neutral)</option>
              </optgroup>
              <optgroup label="Numeric Types">
                <option value={AnswerType.NUMBER}>Number</option>
                <option value={AnswerType.CURRENCY}>Currency Amount ₹</option>
                <option value={AnswerType.PERCENTAGE}>Percentage (%)</option>
                <option value={AnswerType.DURATION_IN_MINUTES}>Duration (minutes)</option>
              </optgroup>
              <optgroup label="Text Types">
                <option value={AnswerType.TEXT_SHORT}>Short Text (50 Chars)</option>
                <option value={AnswerType.TEXT_LONG}>Summary (500 Chars)</option>
              </optgroup>
            </select>
          </div>

          {/* Options for enum types */}
          {isEnumType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Answer Options <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {options.map((option, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => {
                        const newOptions = [...options];
                        newOptions[index] = e.target.value;
                        setOptions(newOptions);
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    <button
                      onClick={() => removeOption(index)}
                      className="text-red-500 hover:text-red-700 p-1"
                    >
                      <HiTrash size={16} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Add new option"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    onKeyDown={(e) => e.key === 'Enter' && addOption()}
                  />
                  <button
                    onClick={addOption}
                    className="text-emerald-500 hover:text-emerald-700 p-1"
                    disabled={!newOption.trim()}
                  >
                    <HiPlus size={16} />
                  </button>
                </div>
              </div>
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500">
                  Add 2-10 options. "Other" option will be automatically included.
                </p>
                <p className={`text-xs ${
                  options.length < 2 ? 'text-red-500' : 'text-gray-400'
                }`}>
                  {options.length}/2 min
                </p>
              </div>
              {options.length < 2 && (
                <p className="text-xs text-red-500 mt-1">At least 2 options are required</p>
              )}
            </div>
          )}

          {/* NPS Configuration */}
          {isNPSType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                NPS Categories
              </label>
              <div className="grid grid-cols-1 gap-3">
                {/* Detractors */}
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <span className="text-lg">😠</span>
                    </div>
                    <div>
                      <div className="font-semibold text-red-800 text-sm">Detractors</div>
                      <div className="text-red-600 text-xs">Unhappy customers</div>
                    </div>
                  </div>
                  <div className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs font-medium">
                    0-6
                  </div>
                </div>

                {/* Passives */}
                <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                      <span className="text-lg">😐</span>
                    </div>
                    <div>
                      <div className="font-semibold text-yellow-800 text-sm">Passives</div>
                      <div className="text-yellow-600 text-xs">Neutral customers</div>
                    </div>
                  </div>
                  <div className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                    7-8
                  </div>
                </div>

                {/* Promoters */}
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-lg">😊</span>
                    </div>
                    <div>
                      <div className="font-semibold text-green-800 text-sm">Promoters</div>
                      <div className="text-green-600 text-xs">Happy advocates</div>
                    </div>
                  </div>
                  <div className="bg-green-200 text-green-800 px-2 py-1 rounded text-xs font-medium">
                    9-10
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Scale Configuration */}
          {isScaleType && (
            <div className="space-y-4">
              {/* Scale Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scale Range
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={config.min || ""}
                    onChange={(e) => setConfig({
                      ...config,
                      min: parseInt(e.target.value) || 1
                    })}
                    min="0"
                    max="99"
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-center"
                  />
                  <span className="text-gray-500 font-medium">to</span>
                  <input
                    type="number"
                    value={config.max || ""}
                    onChange={(e) => setConfig({
                      ...config,
                      max: parseInt(e.target.value) || 5
                    })}
                    min="1"
                    max="100"
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-center"
                  />
                </div>
              </div>

              {/* Labels (Optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Labels (Optional)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={config.labels?.[0] || ""}
                    onChange={(e) => setConfig({
                      ...config,
                      labels: [e.target.value, config.labels?.[1] || ""]
                    })}
                    placeholder="Poor"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <input
                    type="text"
                    value={config.labels?.[1] || ""}
                    onChange={(e) => setConfig({
                      ...config,
                      labels: [config.labels?.[0] || "", e.target.value]
                    })}
                    placeholder="Excellent"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Currency Configuration */}
          {isCurrencyType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Currency
              </label>
              <select
                value={config.currency || "INR"}
                onChange={(e) => setConfig({ ...config, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="INR">INR (₹)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>
          )}

          {/* Category and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as MetricCategory)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {Object.values(MetricCategory).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as MetricPriority)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {Object.values(MetricPriority).map(prio => (
                  <option key={prio} value={prio}>
                    {prio.charAt(0).toUpperCase() + prio.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (validationError) setValidationError(null);
              }}
              placeholder="Why this metric matters for this specific campaign..."
              rows={3}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                reason.length > 0 && reason.length < 20 ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              maxLength={1000}
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                Explain how this metric will help measure campaign success
              </p>
              <p className={`text-xs ${
                reason.length < 20 ? 'text-red-500' : 'text-gray-400'
              }`}>
                {reason.length}/20 min
              </p>
            </div>
            {reason.length > 0 && reason.length < 20 && (
              <p className="text-xs text-red-500 mt-1">Reason must be at least 20 characters</p>
            )}
          </div>

          {/* Extraction Hints */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Extraction Hints (Optional)
            </label>
            <input
              type="text"
              value={extractionHints}
              onChange={(e) => setExtractionHints(e.target.value)}
              placeholder="keywords, phrases, indicators"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Keywords that help AI identify the answer in conversation
            </p>
          </div>

          {/* Validation Summary */}
          {(question.length > 0 || reason.length > 0) && !!validateForm() && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700 font-medium mb-2">Complete these requirements to add your question:</p>
              <ul className="text-sm text-blue-600 space-y-1">
                {question.length < 10 && (
                  <li>• Question needs {10 - question.length} more characters</li>
                )}
                {reason.length < 20 && (
                  <li>• Reason needs {20 - reason.length} more characters</li>
                )}
                {isEnumType && options.length < 2 && (
                  <li>• Add {2 - options.length} more option{2 - options.length === 1 ? '' : 's'}</li>
                )}
              </ul>
            </div>
          )}

          {/* Validation Error Display */}
          {validationError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">{validationError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
              isSubmitting || !!validateForm()
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700'
            }`}
            disabled={isSubmitting || !!validateForm()}
            title={!!validateForm() ? "Please complete the required fields above" : "Add this question to your campaign"}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Adding...
              </>
            ) : !!validateForm() ? (
              'Complete Required Fields'
            ) : (
              'Add Question'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomMetricModal;
